import { cn } from '@/lib/cn';

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

const TONE_CLASSES: Readonly<Record<BadgeTone, string>> = {
  neutral: 'bg-surface-sunken text-fg-muted border-border',
  accent: 'bg-accent-soft text-accent-soft-fg border-accent-line',
  success:
    'bg-[var(--color-success-soft)] text-[var(--color-success)] border-[var(--color-success)]/35',
  warning:
    'bg-[var(--color-warning-soft)] text-[var(--color-warning)] border-[var(--color-warning)]/35',
  danger: 'bg-[var(--color-danger-soft)] text-[var(--color-danger)] border-[var(--color-danger)]/35',
  info: 'bg-[var(--color-info-soft)] text-[var(--color-info)] border-[var(--color-info)]/35',
};

export interface BadgeProps {
  readonly tone?: BadgeTone | undefined;
  readonly className?: string | undefined;
  readonly children: React.ReactNode;
  readonly title?: string | undefined;
}

export function Badge({ tone = 'neutral', className, children, title }: BadgeProps): React.JSX.Element {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium whitespace-nowrap',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** A tag chip. Lowercase and monospace so a tag is visibly a machine value, not prose. */
export function TagChip({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded border border-border bg-surface-sunken px-1.5 py-0.5 font-mono text-[11px] text-fg-muted">
      {children}
    </span>
  );
}
